/**
 * OrgMemberCard — member list item with avatar, name, role badge, and actions.
 */
import { useState } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { impact, ImpactStyle } from '@/lib/utils/haptics'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { UserMinus } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import type { OrganizationMember } from '@/lib/types/organization.types'

interface OrgMemberCardProps {
  member: OrganizationMember
  isCurrentUser: boolean
  canManage: boolean
  onRemove?: (memberId: string) => void
}

export function OrgMemberCard({ member, isCurrentUser, canManage, onRemove }: OrgMemberCardProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const displayName =
    member.profile?.displayName ??
    [member.profile?.firstName, member.profile?.lastName].filter(Boolean).join(' ') ??
    member.profile?.email ??
    '—'

  const roleLabel = member.role === 'fleet_manager' ? t('org.role_manager') : t('org.role_driver')
  const roleVariant = member.role === 'fleet_manager' ? 'info' : 'default'

  const isPending = !member.acceptedAt

  // Alert.alert is an empty no-op on react-native-web, so on app.tago.rent
  // this button did nothing at all — a fleet manager could not remove anyone.
  const [confirmRemove, setConfirmRemove] = useState(false)

  const handleRemove = () => {
    impact(ImpactStyle.Light)
    setConfirmRemove(true)
  }

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        backgroundColor: colors.surface,
        gap: SPACING.md,
        ...WEB_SHADOWS.sm,
      }}
    >
      {/* Avatar */}
      <View style={{ position: 'relative' }}>
        <Avatar
          size="md"
          source={member.profile?.avatarUrl ?? undefined}
          fallback={displayName}
          accessibilityLabel={displayName}
        />
        {isPending && (
          <View
            style={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: 14,
              height: 14,
              borderRadius: 7,
              backgroundColor: colors.warning,
              borderWidth: 2,
              borderColor: colors.surface,
            }}
          />
        )}
      </View>

      {/* Name + role */}
      <View style={{ flex: 1 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
          <Text
            style={{
              fontSize: 15,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
              flexShrink: 1,
            }}
            numberOfLines={1}
          >
            {displayName}
          </Text>
          {isCurrentUser && (
            <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
              ({t('common.you')})
            </Text>
          )}
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: SPACING.xs,
            marginTop: SPACING.xs,
          }}
        >
          <Badge label={roleLabel} variant={roleVariant} size="sm" />
          {isPending && <Badge label={t('common.pending')} variant="warning" size="sm" />}
        </View>
      </View>

      {/* Actions */}
      {canManage && !isCurrentUser && (
        <Pressable
          onPress={handleRemove}
          accessibilityRole="button"
          accessibilityLabel={t('org.remove_member')}
          style={{
            width: 44,
            height: 44,
            borderRadius: RADIUS.md,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <UserMinus size={20} color={colors.textMuted} strokeWidth={1.5} />
        </Pressable>
      )}
      <ConfirmDialog
        visible={confirmRemove}
        title={t('org.remove_member')}
        message={t('org.remove_confirm', { name: displayName })}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          setConfirmRemove(false)
          onRemove?.(member.id)
        }}
        onCancel={() => setConfirmRemove(false)}
        testIDPrefix="org.remove-member"
      />
    </View>
  )
}
